import { useSelector, useDispatch } from "react-redux";
import {
  useForm,
  SubmitHandler,
  FieldValues,
  Controller,
} from "react-hook-form";
import { useNavigate } from "react-router-dom";
// mui
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  useTheme,
  IconButton,
  Typography,
} from "@mui/material";
// components
import CustomTextField from "components/mui/text-field/text-field";
import { Button } from "components/mui/button";
// enums
import { UserType } from "enums/account";
// utils
import {
  AddSubscriptionProps,
  Subscription,
  SubscriptionStatus,
} from "./types/main";
import { postRequest } from "utils/request";
// redux
import { uiActions } from "store/ui/ui-slice";
// assets
import CloseIcon from "@mui/icons-material/Close";
import styles from "assets/scss/editing.module.scss";

const AddSubscription = ({ onClose }: AddSubscriptionProps) => {
  const {
    register,
    handleSubmit,
    getValues,
    reset,
    setValue,
    control,
    watch,
    formState: { errors },
  } = useForm<Subscription>({
    mode: "onChange",
  });

  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const app = useSelector((state: any) => state.app?.allData || {});

  const updateHookForm = (data: Subscription) => {
    setValue("account_type", data["account_type"]);
    setValue("tier", data["tier"]);
    setValue("member_count", data["member_count"]);
    setValue("invoice_count", data["invoice_count"]);
    setValue("project_count", data["project_count"]);
    setValue("milestone_count", data["milestone_count"]);
    setValue("report_count", data["report_count"]);
    setValue("task_count", data["task_count"]);
    setValue("client_count", data["client_count"]);
    setValue("task_history_days", data["task_history_days"]);
    setValue(
      "time_tracking_report_history_days",
      data["time_tracking_report_history_days"],
    );
    setValue(
      "task_details_report_history_days",
      data["task_details_report_history_days"],
    );
    setValue("screenshots_lifetime", data["screenshots_lifetime"]);
    setValue("rank", data["rank"]);
    setValue("price", data["price"]);
    setValue("discount_amount", data["discount_amount"]);
    setValue("account_type", data["account_type"]);
  };
  const handleAddSubscription: SubmitHandler<FieldValues> = async (data) => {
    const requiredFields = ["account_type", "tier", "price", "discount_amount"];

    const normalizedData = Object.fromEntries(
      Object.entries(data).map(([key, value]) => {
        if (requiredFields.includes(key)) {
          return [key, value];
        }

        if (value === "" || value === null || value === undefined) {
          return [key, -1];
        }

        return [key, value];
      }),
    );
    const dataToSend = {
      ...normalizedData,
      creator_member_id: app.memberId,
      entity_id: app.entityId,
    };
    const url = `subscriptions/`;
    const response = await postRequest(url, dataToSend, navigate, {
      "Content-Type": "application/json",
    });
    if (response?.data) {
      if (response.data.isOk) {
        updateHookForm(response.data.data);
        onClose();
      }
      dispatch(
        uiActions.updateNotification({
          message: response?.data.message || "Failed to add subscription.",
          level: response?.data.isOk ? "success" : "error",
        }),
      );
    }
  };

  return (
    <Dialog
      open
      PaperProps={{
        component: "form",
      }}
      className={styles["popup-base"]}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          color: theme.palette.primary.main,
        }}
      >
        Add Subscription
        <IconButton
          onClick={onClose}
          sx={{
            width: "auto",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ color: theme.custom.font.light, fontSize: "14px" }}>
          Note: -1 indicates unlimited
        </Typography>
        <FormControl fullWidth margin="normal" className={styles["menu"]}>
          <InputLabel>Account Type</InputLabel>
          <Controller
            name="account_type"
            control={control}
            defaultValue={getValues("account_type")}
            rules={{
              required: "Account type is required",
            }}
            render={({ field }) => (
              <Select {...field} label="account_type">
                {Object.entries(UserType)
                  .filter(([key]) => isNaN(Number(key)))
                  .map(([key, value]) => {
                    return (
                      <MenuItem key={value} value={value.toString()}>
                        {key}
                      </MenuItem>
                    );
                  })}
              </Select>
            )}
          />
        </FormControl>

        <CustomTextField
          {...register("tier", { required: "Tier is required" })}
          fullWidth
          margin="normal"
          type="text"
          label="Tier"
          name="tier"
          variant="outlined"
          error={!!errors.tier}
          helperText={errors.tier?.message?.toString()}
        />
        <FormControl fullWidth margin="normal" className={styles["menu"]}>
          <InputLabel>Status</InputLabel>
          <Controller
            name="status"
            control={control}
            defaultValue={Number(getValues("status"))}
            rules={{}}
            render={({ field }) => (
              <Select {...field} label="Status">
                {Object.entries(SubscriptionStatus)
                  .filter(([key]) => isNaN(Number(key)))
                  .map(([key, value]) => {
                    return (
                      <MenuItem key={value} value={value.toString()}>
                        {key}
                      </MenuItem>
                    );
                  })}
              </Select>
            )}
          />
        </FormControl>
        {/* <CustomTextField
          {...register("rank", {
            min: {
              value: 1,
              message: "Rank can't be less than 1",
            },
          })}
          width="100%"
          type="number"
          label="Rank"
          name="rank"
          variant="outlined"
          error={!!errors.rank}
          helperText={errors.rank?.message?.toString()}
        /> */}
        <CustomTextField
          {...register("price", {
            required: "Price is required.",
            min: {
              value: 0,
              message: "Price can't be less than 0",
            },
          })}
          width="100%"
          type="number"
          label="Price"
          name="price"
          variant="outlined"
          error={!!errors.price}
          helperText={errors.price?.message?.toString()}
        />
        <CustomTextField
          {...register("discount_amount", {
            required: "Discount amount is required.",
            min: {
              value: 0,
              message: "Discount amount can't be less than 0",
            },
          })}
          width="100%"
          type="number"
          label="Discount amount"
          name="discount_amount"
          variant="outlined"
          error={!!errors.discount_amount}
          helperText={errors.discount_amount?.message?.toString()}
        />
        <CustomTextField
          {...register("member_count", {
            min: {
              value: -1,
              message: "Member count can't be less than -1",
            },
          })}
          width="100%"
          type="number"
          label="Member count"
          name="member_count"
          variant="outlined"
          error={!!errors.member_count}
          helperText={errors.member_count?.message?.toString()}
        />
        <CustomTextField
          {...register("client_count", {
            min: {
              value: -1,
              message: "Client count can't be less than -1",
            },
          })}
          width="100%"
          type="number"
          label="Client count"
          name="client_count"
          variant="outlined"
          error={!!errors.client_count}
          helperText={errors.client_count?.message?.toString()}
        />
        <CustomTextField
          {...register("project_count", {
            min: {
              value: -1,
              message: "Project count can't be less than -1",
            },
          })}
          width="100%"
          type="number"
          label="Project count"
          name="project_count"
          variant="outlined"
          error={!!errors.project_count}
          helperText={errors.project_count?.message?.toString()}
        />
        <CustomTextField
          {...register("milestone_count", {
            min: {
              value: -1,
              message: "Milestone count can't be less than -1",
            },
          })}
          width="100%"
          type="number"
          label="Milestone count"
          name="milestone_count"
          variant="outlined"
          error={!!errors.milestone_count}
          helperText={errors.milestone_count?.message?.toString()}
        />
        <CustomTextField
          {...register("task_count", {
            min: {
              value: -1,
              message: "Task count can't be less than -1",
            },
          })}
          width="100%"
          type="number"
          label="Task count"
          name="task_count"
          variant="outlined"
          error={!!errors.task_count}
          helperText={errors.task_count?.message?.toString()}
        />
        <CustomTextField
          {...register("invoice_count", {
            min: {
              value: -1,
              message: "Invoice count can't be less than -1",
            },
          })}
          width="100%"
          type="number"
          label="Invoice count"
          name="invoice_count"
          variant="outlined"
          error={!!errors.invoice_count}
          helperText={errors.invoice_count?.message?.toString()}
        />
        <CustomTextField
          {...register("report_count", {
            min: {
              value: -1,
              message: "Report count can't be less than -1",
            },
          })}
          width="100%"
          type="number"
          label="Report count"
          name="report_count"
          variant="outlined"
          error={!!errors.report_count}
          helperText={errors.report_count?.message?.toString()}
        />
        <CustomTextField
          {...register("task_history_days", {
            min: {
              value: -1,
              message: "Task history days can't be less than -1",
            },
          })}
          width="100%"
          type="number"
          label="Task history days"
          name="task_history_days"
          variant="outlined"
          error={!!errors.task_history_days}
          helperText={errors.task_history_days?.message?.toString()}
        />
        <CustomTextField
          {...register("time_tracking_report_history_days", {
            min: {
              value: -1,
              message:
                "Time tracking report history days days can't be less than -1",
            },
          })}
          width="100%"
          type="number"
          label="Time tracking report history days"
          name="time_tracking_report_history_days"
          variant="outlined"
          error={!!errors.time_tracking_report_history_days}
          helperText={errors.time_tracking_report_history_days?.message?.toString()}
        />
        <CustomTextField
          {...register("task_details_report_history_days", {
            min: {
              value: -1,
              message:
                "Task details report history days days can't be less than -1",
            },
          })}
          width="100%"
          type="number"
          label="Task details report history days"
          name="task_details_report_history_days"
          variant="outlined"
          error={!!errors.task_details_report_history_days}
          helperText={errors.task_details_report_history_days?.message?.toString()}
        />
        <CustomTextField
          {...register("screenshots_lifetime", {
            min: {
              value: -1,
              message: "Screenshots lifetime can't be less than -1",
            },
          })}
          width="100%"
          type="number"
          label="Screenshots lifetime"
          name="screenshots_lifetime"
          variant="outlined"
          error={!!errors.screenshots_lifetime}
          helperText={errors.screenshots_lifetime?.message?.toString()}
        />
      </DialogContent>
      <DialogActions className={styles["actions"]}>
        <Button
          sx={{
            color: theme.custom.neutralColors.color11,
            backgroundColor: theme.palette.primary.main,
          }}
          onClick={handleSubmit(handleAddSubscription)}
          label="Add"
          className={styles["btn"]}
        />
        <Button
          sx={{
            color: theme.palette.primary.main,
            backgroundColor: "#fff",
            border: `1px solid ${theme.palette.primary.main}`,
          }}
          onClick={onClose}
          label="Cancel"
          className={styles["btn"]}
        />
      </DialogActions>
    </Dialog>
  );
};
export default AddSubscription;
